import type { CrewlinkClient } from '../supabase-client';
import type { CaptureOrganizationRow } from '../database.types';

/**
 * Writer wrapper for `set_capture_organization_fields` (migrations 0010, 0012).
 * M7 / S07-03.
 *
 * The layering rule (CLAUDE.md §8, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. The screen never calls `.rpc(...)`, never updates
 * `capture_organization` directly — no application role holds that grant — and
 * **never writes a Work Log event**, which is what keeps the correction history
 * unforgeable.
 *
 * **THE DATABASE DECIDES WHAT ACTUALLY CHANGED, NOT THIS FILE.** Passing a
 * field means "here is its value"; the writer compares it against what is
 * stored and only a genuinely different value is written, becomes human-owned,
 * or produces history. That matters because the alternative — trusting the
 * screen's comparison — is how a resubmitted-but-untouched location silently
 * became human-owned and locked AI out of it forever (0012's header).
 *
 * **NOT SUPPLIED IS NOT THE SAME AS CLEARED**, and one null cannot express
 * both, so each field carries its own flag.
 */
export async function setCaptureOrganizationFields(
  client: CrewlinkClient,
  captureId: string,
  fields: {
    readonly location?: { readonly value: string | null };
    readonly caption?: { readonly value: string | null };
  },
): Promise<CaptureOrganizationRow | null> {
  const { data, error } = await client.rpc('set_capture_organization_fields', {
    p_capture_id: captureId,
    p_set_location: fields.location !== undefined,
    p_location_display: fields.location?.value ?? null,
    p_set_caption: fields.caption !== undefined,
    p_caption: fields.caption?.value ?? null,
  });

  if (error) throw error;
  // NULL IS A REAL ANSWER: a no-op against a Capture that has no organization
  // row writes nothing and creates nothing, so there is no row to return.
  return data ?? null;
}

/**
 * Turn a failure into something Brandon can act on.
 *
 * There is no stale case here, deliberately: a correction is not derived from a
 * transcript version, so it cannot be stale against one (0010, 0012).
 */
export function describeOrganizationCorrectionError(message: string): string {
  const normalised = message.toLowerCase();

  if (normalised.includes('not found') || normalised.includes('p0002')) {
    return 'That Capture is no longer available.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not save that correction. Please try again.';
}
